// Adaptive difficulty system types for AI Phase 2
// Implements Item Response Theory (IRT) based ability estimation and challenge selection
import { z } from "zod";
import { SkillKeySchema, DisciplineSlugSchema } from "./skills.js";
// Re-export for convenience
export { getDisciplineForSkillKey, validateSkillTagsForDiscipline } from "./skills.js";
// ==================== Configuration Constants ====================
/**
 * Adaptive system configuration constants
 * These define the core parameters of the IRT-based difficulty system
 */
export const AdaptiveConfig = {
    /** Target probability of success (Zone of Proximal Development sweet spot) */
    TARGET_SUCCESS_RATE: 0.75,
    /** Minimum samples before confidence is meaningful */
    MIN_SAMPLES: 5,
    /** Bounds for theta (ability) values on the IRT scale */
    THETA_BOUNDS: [-3, 3],
    /** Base learning rate for ability updates (decreases with confidence) */
    LEARNING_RATE_BASE: 0.3,
    /** Initial theta for new abilities */
    INITIAL_THETA: 0,
    /** Initial confidence for new abilities */
    INITIAL_CONFIDENCE: 0.1,
    /** Maximum confidence (never reaches 1.0 to allow continued learning) */
    MAX_CONFIDENCE: 0.95,
    /** Confidence increment per sample */
    CONFIDENCE_INCREMENT: 0.02,
    /** Number of recent challenges to exclude from selection */
    RECENT_CHALLENGE_EXCLUSION_COUNT: 5,
    /** Number of top candidates to randomly select from for variety */
    TOP_CANDIDATES_FOR_SELECTION: 3,
};
/**
 * Helper function to convert theta to parent-friendly ability level
 * @param theta - Ability parameter on IRT scale
 * @returns Human-readable ability level
 */
export function thetaToAbilityLevel(theta) {
    if (theta < -1)
        return "beginner";
    if (theta < 0.5)
        return "developing";
    if (theta < 1.5)
        return "strong";
    return "expert";
}
/**
 * Helper function to get description for ability level
 * @param level - The ability level
 * @returns Human-readable description
 */
export function getAbilityLevelDescription(level) {
    switch (level) {
        case "beginner":
            return "Just getting started";
        case "developing":
            return "Making good progress";
        case "strong":
            return "Well understood";
        case "expert":
            return "Mastered!";
    }
}
// ==================== Play Mode & Settings Types ====================
/**
 * Play mode determining challenge selection strategy
 */
export var PlayMode;
(function (PlayMode) {
    /** Challenges presented in order, level by level */
    PlayMode["SEQUENTIAL"] = "sequential";
    /** Smart challenge selection based on child's abilities */
    PlayMode["ADAPTIVE"] = "adaptive";
})(PlayMode || (PlayMode = {}));
// ==================== Zod Validation Schemas ====================
/** ISO datetime string schema */
const ISODateTimeSchema = z.string().refine((val) => {
    const date = new Date(val);
    return !isNaN(date.getTime());
}, { message: "Invalid ISO datetime string" });
/** Schema for ability estimate */
export const AbilityEstimateSchema = z.object({
    theta: z.number().min(-3).max(3),
    confidence: z.number().min(0).max(1),
    sampleSize: z.number().int().min(0),
    lastUpdated: ISODateTimeSchema,
});
/** Schema for ability level */
export const AbilityLevelSchema = z.enum(["beginner", "developing", "strong", "expert"]);
/** Schema for play mode */
export const PlayModeSchema = z.enum(["sequential", "adaptive"]);
/** Schema for child settings */
export const ChildSettingsSchema = z.object({
    id: z.string().uuid(),
    childId: z.string().uuid(),
    playMode: PlayModeSchema,
    adaptiveTargetSuccessRate: z.number().min(0.65).max(0.85),
    skipMasteredChallenges: z.boolean(),
    minChallengesForAdaptation: z.number().int().min(1),
    createdAt: ISODateTimeSchema,
    updatedAt: ISODateTimeSchema,
});
/** Schema for play session */
export const PlaySessionSchema = z.object({
    id: z.string().uuid(),
    childId: z.string().uuid(),
    disciplineId: z.string().uuid().nullable(),
    playMode: PlayModeSchema,
    startedAt: ISODateTimeSchema,
    endedAt: ISODateTimeSchema.nullable(),
    challengesAttempted: z.number().int().min(0),
    challengesCompleted: z.number().int().min(0),
    avgSuccessRate: z.number().min(0).max(1).nullable(),
    sessionMetadata: z.record(z.string(), z.unknown()),
});
/** Schema for session challenge */
export const SessionChallengeSchema = z.object({
    id: z.string().uuid(),
    sessionId: z.string().uuid(),
    challengeId: z.string().uuid(),
    position: z.number().int().min(1),
    skillKey: SkillKeySchema.nullable(),
    predictedSuccessRate: z.number().min(0).max(1).nullable(),
    selectionReason: z.string().nullable(),
    actualResult: z.boolean().nullable(),
    timeSpentSeconds: z.number().int().min(0).nullable(),
    attempts: z.number().int().min(1).nullable(),
    startedAt: ISODateTimeSchema,
    completedAt: ISODateTimeSchema.nullable(),
});
/** Schema for calibrated challenge */
export const CalibratedChallengeSchema = z.object({
    id: z.string().uuid(),
    levelId: z.string().uuid(),
    challengeNumber: z.number().int().min(1),
    type: z.string(),
    content: z.record(z.string(), z.unknown()),
    difficulty: z.number().int().min(1).max(5),
    isActive: z.boolean(),
    skillTags: z.array(SkillKeySchema),
    calibratedDifficulty: z.number().min(-3).max(3),
    difficultyConfidence: z.number().min(0).max(1),
    expectedTimeSeconds: z.number().int().min(1).nullable(),
});
/** Schema for challenge candidate */
export const ChallengeCandidateSchema = z.object({
    challenge: CalibratedChallengeSchema,
    suitabilityScore: z.number().min(0).max(1),
    predictedSuccessRate: z.number().min(0).max(1),
    selectionReason: z.string(),
});
/** Schema for selection criteria */
export const SelectionCriteriaSchema = z.object({
    childId: z.string().uuid(),
    discipline: DisciplineSlugSchema.nullable(),
    currentLevel: z.number().int().min(1).nullable(),
    recentChallenges: z.array(z.string().uuid()),
    targetSuccessRate: z.number().min(0.5).max(0.95).optional(),
});
/** Schema for skill ability summary */
export const SkillAbilitySummarySchema = z.object({
    skillKey: SkillKeySchema,
    displayName: z.string(),
    theta: z.number().min(-3).max(3),
    abilityLevel: AbilityLevelSchema,
    confidence: z.number().min(0).max(1),
    sampleSize: z.number().int().min(0),
    needsMorePractice: z.boolean(),
});
/** Schema for discipline ability summary */
export const DisciplineAbilitySummarySchema = z.object({
    disciplineId: z.string().uuid(),
    disciplineSlug: DisciplineSlugSchema,
    disciplineName: z.string(),
    averageTheta: z.number().min(-3).max(3),
    abilityLevel: AbilityLevelSchema,
    averageConfidence: z.number().min(0).max(1),
    totalSamples: z.number().int().min(0),
    skills: z.array(SkillAbilitySummarySchema),
    weakestSkills: z.array(SkillAbilitySummarySchema),
    strongestSkills: z.array(SkillAbilitySummarySchema),
});
/** Schema for ability summary */
export const AbilitySummarySchema = z.object({
    childId: z.string().uuid(),
    childName: z.string(),
    lastUpdated: ISODateTimeSchema,
    disciplines: z.array(DisciplineAbilitySummarySchema),
    overallAbilityLevel: AbilityLevelSchema,
    overallTheta: z.number().min(-3).max(3),
    tips: z.array(z.string()),
});
//# sourceMappingURL=adaptive.js.map